export interface FormOption {
  label: string;
  value: string | null;
}

type DropdownControl = {
  key: string;
  type: 'dropdown';
  label: string;
  options: FormOption[];
};

type RadioControl = {
  key: string;
  type: 'radio';
  label: string;
  options: FormOption[];
};

type CounterControl = {
  key: string;
  type: 'counter';
  label: string;
  min: number;
  max: number;
};

export type FormControl = DropdownControl | RadioControl | CounterControl;

export interface FormSectionConfig {
  key: string;
  title: string;
  icon: string;

  counter?: {
    min: number;
    max: number;
  };

  group?: {
    titlePrefix: string;
    controls: FormControl[];

    copyFirstGroup: {
      enabled: boolean;
      options: {
        value: string;
        text: string;
      }[];
    };
  };

  controls?: FormControl[];
}

const yesNoOptions = [
  {
    value: 'yes',
    text: 'Yes',
  },
  {
    value: 'no',
    text: 'No',
  },
];

export const washroomConfig: FormSectionConfig[] = [
  {
    key: 'basin_tap_count',
    title: 'Tell us about the taps',
    icon: 'sink',

    counter: {
      min: 0,
      max: 4,
    },

    group: {
      titlePrefix: 'Tap',

      controls: [
        {
          key: 'tap_type',
          type: 'dropdown',
          label: 'What is the type of tap?',
          options: [
            {
              value: '',
              label: 'What is the type of tap?',
            },
            {
              value: 'mixer_bathroom_basin_tap_count',
              label: 'questions.mixer',
            },
            {
              value: 'pillar_bathroom_basin_tap_count',
              label: 'questions.question.28.meta.73.74.text',
            },
            {
              value: 'pillar_percussion_bathroom_basin_tap_count',
              label: 'questions.question.28.meta.73.76.text',
            },
            {
              value: 'sensor_mixer_bathroom_basin_tap_count',
              label: 'questions.question.28.meta.73.77.text',
            },
          ],
        },
        {
          key: 'tap_leak',
          type: 'dropdown',
          label: 'Is the tap dripping?',
          options: [
            {
              value: '',
              label: 'Is the tap dripping?',
            },
            {
              value: 'bathroom_dripping_slow_count',
              label: 'questions.question.31.meta.189.description',
            },
            {
              value: 'bathroom_dripping_moderately_count',
              label: 'questions.question.31.meta.190.description',
            },
            {
              value: 'bathroom_dripping_alot_count',
              label: 'questions.question.31.meta.191.description',
            },
            {
              value: null,
              label: 'No leak',
            },
          ],
        },

        {
          key: 'tap_leak_flow',
          type: 'dropdown',
          label: 'How strong is the flow of water?',
          options: [
            {
              value: '',
              label: 'How strong is the flow of water?',
            },
            {
              value: 'weak_flow_bathroom_basin_tap',
              label: 'questions.weak',
            },
            {
              value: 'medium_flow_bathroom_basin_tap',
              label: 'questions.question.29.meta.80.81.text',
            },
            {
              value: 'strong_flow_bathroom_basin_tap',
              label: 'questions.question.29.meta.80.82.text',
            },
          ],
        },
      ],

      copyFirstGroup: {
        enabled: false,
        options: yesNoOptions,
      },
    },
  },
  {
    key: 'toilet_count',
    title: 'Tell us about the toilets',
    icon: 'toilet',

    counter: {
      min: 0,
      max: 4,
    },

    group: {
      titlePrefix: 'Toilet',

      controls: [
        {
          key: 'toilet_type',
          type: 'dropdown',
          label: 'What is the type of toilet?',
          options: [
            {
              value: '',
              label: 'What is the type of toilet?',
            },
            {
              value: 'single_lever_flush_toilet_type_count',
              label: 'questions.question.22.meta.52.52.text',
            },
            {
              value: '1button_flush_toilet_type_count',
              label: 'questions.question.22.meta.52.53.text',
            },
            {
              value: '2button_flush_toilet_type_count',
              label: 'questions.question.22.meta.52.54.text',
            },
            {
              value: 'sensor_flush_toilet_type_count',
              label: 'questions.question.22.meta.52.55.text',
            },
          ],
        },

        {
          key: 'toilet_leak_flow',
          type: 'dropdown',
          label: 'Is the toilet leaking?',
          options: [
            {
              value: '',
              label: 'Is the toilet leaking?',
            },
            {
              value: 'toilet_leak_flow_slight_ripple_count',
              label: 'questions.question.27.meta.186.description',
            },
            {
              value: 'toilet_leak_flow_evident_count',
              label: 'questions.question.27.meta.187.description',
            },
            {
              value: 'toilet_leak_flow_nonstop_count',
              label: 'questions.question.27.meta.188.description',
            },
            {
              value: null,
              label: 'Tested, no leak',
            },
          ],
        },
      ],

      copyFirstGroup: {
        enabled: false,
        options: yesNoOptions,
      },
    },
  },
  {
    key: 'urinal_count',
    title: 'Tell us about the urinals',
    icon: 'urinal',

    counter: {
      min: 0,
      max: 4,
    },

    group: {
      titlePrefix: 'Urinal',

      controls: [
        {
          key: 'urinal_type',
          type: 'dropdown',
          label: 'How are the urinals flushed?',
          options: [
            {
              value: '',
              label: 'How are the urinals flushed?',
            },
            {
              value: 'timed_flush_urinal_type_count',
              label: 'questions.question.25.meta.62.62.text',
            },
            {
              value: 'sensor_flush_urinal_type_count',
              label: 'questions.question.25.meta.62.63.text',
            },
            {
              value: 'waterless_flush_urinal_type_count',
              label: 'questions.question.25.meta.62.64.text',
            },
            {
              value: 'automatic_flush_urinal_type_count',
              label: 'questions.question.25.meta.62.65.text',
            },
            {
              value: 'unknown_flush_urinal_type_count',
              label: 'questions.question.25.meta.62.66.text',
            },
          ],
        },

        {
          key: 'urinal_leak_flow',
          type: 'dropdown',
          label: 'Is the urinal leaking?',
          options: [
            {
              value: '',
              label: 'Is the urinal leaking?',
            },
            {
              value: 'urinal_leak_flow_slight_ripple_count',
              label: 'questions.question.27.meta.186.description',
            },
            {
              value: 'urinal_leak_flow_evident_count',
              label: 'questions.question.27.meta.187.description',
            },
            {
              value: 'urinal_leak_flow_nonstop_count',
              label: 'questions.question.27.meta.188.description',
            },
            {
              value: null,
              label: 'Tested, no leak',
            },
          ],
        },
      ],

      copyFirstGroup: {
        enabled: false,
        options: yesNoOptions,
      },
    },
  },

  {
    key: 'shower_count',
    title: 'Tell us about the showers',
    icon: 'shower',

    counter: {
      min: 0,
      max: 4,
    },

    group: {
      titlePrefix: 'Shower',

      controls: [
        {
          key: 'shower_type',
          type: 'dropdown',
          label: 'What is the type of shower?',
          options: [
            {
              value: '',
              label: 'What is the type of shower?',
            },
            {
              value: 'gravity_shower_type_count',
              label: 'questions.gravity',
            },
            {
              value: 'pumped_shower_type_count',
              label: 'questions.question.32.meta.89.91.text',
            },
            {
              value: 'elextric_shower_type_count',
              label: 'questions.question.20.meta.37.38.text',
            },
          ],
        },

        {
          key: 'shower_control',
          type: 'dropdown',
          label: 'questions.question.33.text',
          options: [
            {
              value: '',
              label: 'questions.question.33.text',
            },
            {
              value: 'manual_shower_control_count',
              label: 'questions.question.33.meta.99.99.text',
            },
            {
              value: 'percussion_shower_control_count',
              label: 'questions.question.33.meta.99.100.text',
            },
            {
              value: 'sensor_shower_control_count',
              label: 'questions.question.25.meta.62.63.text',
            },
            {
              value: 'electric_shower_control_count',
              label: 'questions.question.20.meta.37.38.text',
            },
          ],
        },

        {
          key: 'shower_leak',
          type: 'dropdown',
          label: 'Is the shower dripping?',
          options: [
            {
              value: '',
              label: 'questions.question.31.text',
            },
            {
              value: 'shower_dripping_slow_count',
              label: 'questions.question.31.meta.189.description',
            },
            {
              value: 'shower_dripping_moderately_count',
              label: 'questions.question.31.meta.190.description',
            },
            {
              value: 'shower_dripping_alot_count',
              label: 'questions.question.31.meta.191.description',
            },
            {
              value: null,
              label: 'No drips',
            },
          ],
        },

        {
          key: 'showers_per_week',
          type: 'counter',
          label: 'Number of showers per person per week',
          min: 1,
          max: 999,
        },
      ],
      copyFirstGroup: {
        enabled: false,
        options: yesNoOptions,
      },
    },
  },
];
